import {
    collectionOptions,
    type DbClient,
    type LiveQueryOptions,
    localOnlyCollectionOptions,
    inArray as tsInArray,
    useLiveQuery,
} from "@tanstack/react-db";
import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import {
    DogSex,
    type DogSizeBucket,
    getLifeStageLabel,
    type ListingSummaryProjection,
} from "@wags/catalog/pure.ts";
import type { DiscoveryListingCard } from "@wags/discovery/index.ts";
import { getCatalog } from "@/lib/core/catalog-core.server";
import { type AdoptionStatus, toAdoptionStatus } from "./adoption-status";
import { listingSummaryHydrationContract } from "./listing-summary.contract.ts";
import { createPendingWorkByKey } from "./pending-work-by-key.ts";

export type { ListingSummaryProjection } from "@wags/catalog/pure.ts";

export type ListingSummaryHydrationInput = Omit<
    ListingSummaryProjection,
    "availableAt" | "birthday"
> & {
    availableAt: Date | string;
    birthday?: Date | string;
};

declare global {
    interface Window {
        __wagsCollections?: Record<string, object>;
    }
}

export const getWagsListingsBatchData = createServerOnlyFn(
    async (data: { listingIds: string[] }) => {
        const catalog = await getCatalog();
        return await catalog.listings.getListingsByIds(data.listingIds);
    },
);

export const getWagsListingsBatch = createServerFn({ method: "POST" })
    .inputValidator((data: { listingIds: string[] }) => data)
    .handler(async ({ data }) => getWagsListingsBatchData(data));

type WagsListingBatchRecord = Awaited<
    ReturnType<typeof getWagsListingsBatchData>
>[number];

type CollectionWagsMedia = {
    id: string;
    mimeType?: string | null;
    url: string | null;
};

type CollectionWagsBreed = {
    id: string;
    name: string;
    slug: string;
    size: string | null;
    coatType: string | null;
    displayName: string | null;
    friendlinessChildren: string | null;
    friendlinessDogs: string | null;
    friendlinessCats: string | null;
    exerciseMinutesDaily: number | null;
    exerciseIntensity: string | null;
    trainability: string | null;
    recommendedExperience: string | null;
    homeCompatibility: string[] | null;
    sheddingLevel: string | null;
    lifespanMinYears: number | null;
    lifespanMaxYears: number | null;

    weightMinLbs: number | null;
    weightMaxLbs: number | null;
};

type CollectionWagsOrganization = {
    id: string;
    isVerified: boolean;
    name: string | null;
    type: string | null;
    source: string | null;
    city: string | null;
    state: string | null;
    location: { x: number; y: number } | null;
    description: string | null;
    media: CollectionWagsMedia[];
};

type CollectionWagsParentDog = {
    id: string;
    name: string;
    media: Array<{ id: string; url: string }>;
};

export type CollectionWagsListing = {
    id: string;
    createdAt: Date;

    status: AdoptionStatus;
    organizationId: string | null;
    primaryPhotoId: string | null;
    title: string | null;
    description: string | null;
    descriptionQa: WagsListingBatchRecord["descriptionQa"];
    priceRange: WagsListingBatchRecord["priceRange"];
    averagePrice: number | null;
    dob: number | null;
    numberOfMalesAvailable: number;
    numberOfFemalesAvailable: number;
    breed: string | null;

    listingBreedNames: string[];

    providerType: string | null;
    organization: CollectionWagsOrganization | null;
    media: CollectionWagsMedia[];
    breeds: CollectionWagsBreed[];
    isGoodWithKids: boolean;
    isGoodWithDogs: boolean;
    isGoodWithCats: boolean;
    healthTests: string[];
    perks: string[];
    transports: Array<{
        id: string;
        type: string | null;
        label: string | null;
    }>;
    isGroundTransportAvailable: boolean;
    isFlightNannyTransportAvailable: boolean;
    isAirCargoTransportAvailable: boolean;
    parentDogs: {
        mom: CollectionWagsParentDog | null;
        dad: CollectionWagsParentDog | null;
    };
    name: string;
    location: { x: number; y: number } | null;
    birthday?: Date;
    ageLabel?: string;
    weightBucket: DogSizeBucket | null;
    sex: DogSex | null;
    imageUrls: string[];
    availableAt: Date;
    distanceMiles: number | null;

    weightMinLbs: number | null;
    weightMaxLbs: number | null;
    summaryHydratedAt: number;
    detailHydratedAt?: number;
};

export function formatBreedNames(listing: {
    listingBreedNames: string[];
    breeds: Array<{ name: string }>;
}) {
    return (
        listing.listingBreedNames.join(", ") ||
        listing.breeds.map((breed) => breed.name).join(", ")
    );
}

export function toDiscoveryCard(
    listing: CollectionWagsListing,
): DiscoveryListingCard {
    return {
        id: listing.id,
        name: listing.name,
        location: listing.location,
        birthday: listing.birthday,
        ageLabel: listing.ageLabel,
        weightBucket: listing.weightBucket,
        sex: listing.sex,
        imageUrls: listing.imageUrls,
        availableAt: listing.availableAt,
        breeds: listing.breeds.map((breed) => ({
            id: breed.slug || breed.id,
            name: breed.name,
        })),
        distanceMiles: listing.distanceMiles,
        providerType: listing.providerType,
        minPrice: listing.priceRange?.min ?? null,
        maxPrice: listing.priceRange?.max ?? null,
        description: listing.description,
    };
}

function toSummaryBreeds(
    breeds: ListingSummaryProjection["breeds"],
): CollectionWagsBreed[] {
    return breeds.map((breed) => ({
        id: breed.id,
        name: breed.name,
        slug: breed.id,
        size: null,

        coatType: null,
        displayName: breed.name,
        friendlinessChildren: null,
        friendlinessDogs: null,
        friendlinessCats: null,
        exerciseMinutesDaily: null,
        exerciseIntensity: null,
        trainability: null,
        recommendedExperience: null,
        homeCompatibility: null,
        sheddingLevel: null,
        lifespanMinYears: null,
        lifespanMaxYears: null,
        weightMinLbs: null,
        weightMaxLbs: null,
    }));
}

function toSummaryMedia(
    listingId: string,
    imageUrls: string[],
): CollectionWagsMedia[] {
    return imageUrls.map((url, index) => ({
        id: `${listingId}-summary-media-${index}`,
        url,
    }));
}

function toDetailBreeds(
    breeds: WagsListingBatchRecord["breeds"],
): CollectionWagsBreed[] {
    return breeds.map((breed) => ({
        id: breed.id,
        name: breed.name,
        slug: breed.slug,
        size: breed.size,
        coatType: breed.coatType,
        displayName: breed.displayName,
        friendlinessChildren: breed.friendlinessChildren,
        friendlinessDogs: breed.friendlinessDogs,
        friendlinessCats: breed.friendlinessCats,
        exerciseMinutesDaily: breed.exerciseMinutesDaily,
        exerciseIntensity: breed.exerciseIntensity,
        trainability: breed.trainability,
        recommendedExperience: breed.recommendedExperience,
        homeCompatibility: breed.homeCompatibility,
        sheddingLevel: breed.sheddingLevel,
        lifespanMinYears: breed.lifespanMinYears,
        lifespanMaxYears: breed.lifespanMaxYears,
        weightMinLbs: breed.weightMinLbs,
        weightMaxLbs: breed.weightMaxLbs,
    }));
}

function toDetailMedia(
    media: WagsListingBatchRecord["media"],
): CollectionWagsMedia[] {
    return media.map((item) => ({
        id: item.id,
        mimeType: item.mimeType,
        url: item.url ?? null,
    }));
}

function toDetailOrganization(
    organization: WagsListingBatchRecord["organization"],
): CollectionWagsOrganization | null {
    if (!organization) {
        return null;
    }

    return {
        id: organization.id,
        isVerified: organization.isVerified,
        name: organization.name,
        type: organization.type,
        source: organization.source,
        city: organization.city,
        state: organization.state,
        location: organization.location,
        description: organization.description,
        media: organization.media.map((item) => ({
            id: item.id,
            mimeType: item.mimeType,
            url: item.url ?? null,
        })),
    };
}

function mergeSummaryIntoListing(
    summary: ListingSummaryHydrationInput,
    existing: CollectionWagsListing | undefined,
): CollectionWagsListing {
    const detailHydratedAt = existing?.detailHydratedAt;
    const { availableAt, birthday } =
        listingSummaryHydrationContract.parse(summary);

    return {
        id: summary.id,
        createdAt: existing?.createdAt ?? availableAt,
        ...mergeSummaryCatalogFields(summary, existing, birthday),
        ...mergeSummaryDetailFields(summary, existing, detailHydratedAt),
        ...mergeSummaryDisplayFields(summary, existing, birthday),
        imageUrls: summary.imageUrls,
        availableAt,
        distanceMiles: summary.distanceMiles,
        summaryHydratedAt: Date.now(),
        detailHydratedAt,
    };
}

function mergeSummaryCatalogFields(
    summary: ListingSummaryHydrationInput,
    existing: CollectionWagsListing | undefined,
    birthday: Date | undefined,
) {
    return {
        status: summary.status ?? existing?.status ?? "available",
        organizationId: existing?.organizationId ?? null,
        primaryPhotoId: existing?.primaryPhotoId ?? null,
        ...mergeSummaryDescriptionFields(summary, existing),
        priceRange: existing?.priceRange ?? null,
        averagePrice: existing?.averagePrice ?? null,
        dob: existing?.dob ?? (birthday ? birthday.getTime() : null),
        ...mergeSummaryAvailabilityFields(summary, existing),
    };
}

function mergeSummaryDescriptionFields(
    summary: ListingSummaryHydrationInput,
    existing: CollectionWagsListing | undefined,
) {
    return {
        title: existing?.title ?? summary.name,
        description: existing?.description ?? null,
        descriptionQa: existing?.descriptionQa ?? [],
    };
}

function mergeSummaryAvailabilityFields(
    summary: ListingSummaryHydrationInput,
    existing: CollectionWagsListing | undefined,
) {
    return {
        numberOfMalesAvailable:
            existing?.numberOfMalesAvailable ??
            (summary.sex === DogSex.Male ? 1 : 0),
        numberOfFemalesAvailable:
            existing?.numberOfFemalesAvailable ??
            (summary.sex === DogSex.Female ? 1 : 0),
        breed: existing?.breed ?? summary.breeds[0]?.name ?? null,
        listingBreedNames:
            existing?.listingBreedNames ??
            summary.breeds.map((breed) => breed.name),
        providerType: summary.providerType ?? existing?.providerType ?? null,
        organization: existing?.organization ?? null,
    };
}

function mergeSummaryDetailFields(
    summary: ListingSummaryHydrationInput,
    existing: CollectionWagsListing | undefined,
    detailHydratedAt: number | undefined,
) {
    return {
        media:
            detailHydratedAt != null
                ? (existing?.media ?? [])
                : toSummaryMedia(summary.id, summary.imageUrls),
        breeds:
            detailHydratedAt != null
                ? (existing?.breeds ?? [])
                : toSummaryBreeds(summary.breeds),
        isGoodWithKids: existing?.isGoodWithKids ?? false,
        isGoodWithDogs: existing?.isGoodWithDogs ?? false,
        isGoodWithCats: existing?.isGoodWithCats ?? false,
        ...mergeSummaryAmenities(existing),
    };
}

function mergeSummaryAmenities(existing: CollectionWagsListing | undefined) {
    return {
        healthTests: existing?.healthTests ?? [],
        perks: existing?.perks ?? [],
        transports: existing?.transports ?? [],
        isGroundTransportAvailable:
            existing?.isGroundTransportAvailable ?? false,
        isFlightNannyTransportAvailable:
            existing?.isFlightNannyTransportAvailable ?? false,
        isAirCargoTransportAvailable:
            existing?.isAirCargoTransportAvailable ?? false,
        parentDogs: existing?.parentDogs ?? { mom: null, dad: null },
    };
}

function mergeSummaryDisplayFields(
    summary: ListingSummaryHydrationInput,
    existing: CollectionWagsListing | undefined,
    birthday: Date | undefined,
) {
    return {
        name: summary.name,
        location: summary.location,
        birthday: birthday ?? existing?.birthday,
        ageLabel: summary.ageLabel ?? existing?.ageLabel,
        weightBucket: summary.weightBucket ?? existing?.weightBucket ?? null,
        weightMinLbs: existing?.weightMinLbs ?? null,
        weightMaxLbs: existing?.weightMaxLbs ?? null,
        sex: summary.sex ?? existing?.sex ?? null,
    };
}

function mergeDetailIntoListing(
    detail: WagsListingBatchRecord,
    existing: CollectionWagsListing | undefined,
): CollectionWagsListing {
    const birthday =
        detail.dob == null ? existing?.birthday : new Date(detail.dob);
    const ageLabel =
        detail.ageLabel ??
        (detail.organization?.type === "shelter_rescue" && detail.dob != null
            ? (getLifeStageLabel(detail.dob) ?? undefined)
            : existing?.ageLabel);

    return {
        id: detail.id,
        createdAt: detail.createdAt,
        ...mergeDetailCatalogFields(detail),
        ...mergeDetailDisplayFields(detail, existing, birthday, ageLabel),
        distanceMiles: existing?.distanceMiles ?? null,
        summaryHydratedAt: existing?.summaryHydratedAt ?? Date.now(),
        detailHydratedAt: Date.now(),
    };
}

function mergeDetailCatalogFields(detail: WagsListingBatchRecord) {
    return {
        status: toAdoptionStatus(detail.status),
        organizationId: detail.organizationId,
        primaryPhotoId: detail.primaryPhotoId,
        title: detail.title,
        description: detail.description,
        descriptionQa: detail.descriptionQa,
        priceRange: detail.priceRange,
        averagePrice: detail.averagePrice,
        dob: detail.dob,
        numberOfMalesAvailable: detail.numberOfMalesAvailable,
        numberOfFemalesAvailable: detail.numberOfFemalesAvailable,
        breed: detail.breed,
        listingBreedNames: detail.breedNames,
        providerType: detail.providerType,
        organization: toDetailOrganization(detail.organization),
        media: toDetailMedia(detail.media),
        breeds: toDetailBreeds(detail.breeds),
        isGoodWithKids: detail.isGoodWithKids,
        isGoodWithDogs: detail.isGoodWithDogs,
        isGoodWithCats: detail.isGoodWithCats,
        healthTests: detail.healthTests,
        perks: detail.perks,
        transports: detail.transports,
        isGroundTransportAvailable: detail.isGroundTransportAvailable,
        isFlightNannyTransportAvailable: detail.isFlightNannyTransportAvailable,
        isAirCargoTransportAvailable: detail.isAirCargoTransportAvailable,
        parentDogs: detail.parentDogs,
    };
}

function mergeDetailDisplayFields(
    detail: WagsListingBatchRecord,
    existing: CollectionWagsListing | undefined,
    birthday: Date | undefined,
    ageLabel: string | undefined,
) {
    return {
        name:
            detail.title?.trim() ||
            existing?.name ||
            detail.breeds[0]?.name ||
            "Dog",
        location: existing?.location ?? detail.organization?.location ?? null,
        birthday,
        ageLabel,
        ...mergeDetailPhysicalFields(detail, existing),
        imageUrls:
            detail.media
                .map((item) => item.url ?? null)
                .filter((url): url is string => Boolean(url)) ?? [],
        availableAt:
            detail.availableAt != null
                ? new Date(detail.availableAt)
                : (existing?.availableAt ?? detail.createdAt),
    };
}

function mergeDetailPhysicalFields(
    detail: WagsListingBatchRecord,
    existing: CollectionWagsListing | undefined,
) {
    return {
        weightBucket: detail.weightBucket ?? existing?.weightBucket ?? null,
        weightMinLbs: detail.weightMinLbs,
        weightMaxLbs: detail.weightMaxLbs,
        sex:
            existing?.sex ??
            (detail.numberOfFemalesAvailable > 0 &&
            detail.numberOfMalesAvailable === 0
                ? DogSex.Female
                : detail.numberOfMalesAvailable > 0 &&
                    detail.numberOfFemalesAvailable === 0
                  ? DogSex.Male
                  : null),
    };
}

export const wagsListingsCollection = collectionOptions("wagsListings", () =>
    localOnlyCollectionOptions<CollectionWagsListing>({
        id: "wagsListings",
        getKey: (item) => item.id,
        initialData: [],
    }),
);

export function getWagsListingsCollection(dbClient: DbClient) {
    const collection = dbClient.collection<CollectionWagsListing>(
        wagsListingsCollection,
    );

    if (import.meta.env?.DEV && typeof window !== "undefined") {
        window.__wagsCollections ??= {};
        window.__wagsCollections.wagsListingsCollection = collection;
    }

    return collection;
}

export function upsertWagsListingSummaries(
    dbClient: DbClient,
    summaries: ListingSummaryHydrationInput[],
) {
    if (summaries.length === 0) {
        return;
    }

    const collection = getWagsListingsCollection(dbClient);
    for (const summary of summaries) {
        const nextListing = mergeSummaryIntoListing(
            summary,
            collection.get(summary.id),
        );

        if (collection.has(summary.id)) {
            collection.update(summary.id, (draft) => {
                Object.assign(draft, nextListing);
            });
        } else {
            collection.insert(nextListing);
        }
    }
}

export function upsertWagsListingRecord(
    dbClient: DbClient,
    listing: CollectionWagsListing,
) {
    const collection = getWagsListingsCollection(dbClient);
    if (collection.has(listing.id)) {
        collection.update(listing.id, (draft) => {
            Object.assign(draft, listing);
        });
    } else {
        collection.insert(listing);
    }
}

export function upsertWagsListingDetails(
    dbClient: DbClient,
    details: WagsListingBatchRecord[],
) {
    if (details.length === 0) {
        return;
    }

    const collection = getWagsListingsCollection(dbClient);
    for (const detail of details) {
        const nextListing = mergeDetailIntoListing(
            detail,
            collection.get(detail.id),
        );

        if (collection.has(detail.id)) {
            collection.update(detail.id, (draft) => {
                Object.assign(draft, nextListing);
            });
        } else {
            collection.insert(nextListing);
        }
    }
}

const WAGS_LISTINGS_COLLECTION_GC_TIME = 5 * 60 * 1000;

export function getWagsListingsLiveQueryOptions(listingIds: string[]) {
    const normalizedListingIds = [
        ...new Set(listingIds.filter(Boolean)),
    ].sort();

    return {
        gcTime: WAGS_LISTINGS_COLLECTION_GC_TIME,
        queryKey: ["wagsListings", "subset", normalizedListingIds],
        query: (queryBuilder) =>
            queryBuilder
                .from({ listings: wagsListingsCollection })
                .where(({ listings }) =>
                    tsInArray(listings.id, normalizedListingIds),
                ),
    } satisfies LiveQueryOptions;
}

const WAGS_LISTINGS_PREFETCH_CHUNK_SIZE = 100;
const WAGS_LISTINGS_PREFETCH_CONCURRENCY = 2;

function createWagsListingsPrefetchState() {
    return {
        activeBatches: 0,
        pendingWork: createPendingWorkByKey(),
        queuedResolvers: [] as Array<() => void>,
    };
}

type WagsListingsPrefetchState = ReturnType<
    typeof createWagsListingsPrefetchState
>;

const prefetchStateByClient = new WeakMap<
    DbClient,
    WagsListingsPrefetchState
>();

function getWagsListingsPrefetchState(dbClient: DbClient) {
    const existing = prefetchStateByClient.get(dbClient);
    if (existing) return existing;

    const state = createWagsListingsPrefetchState();
    prefetchStateByClient.set(dbClient, state);
    return state;
}

async function acquireWagsListingsPrefetchSlot(
    state: WagsListingsPrefetchState,
) {
    if (state.activeBatches < WAGS_LISTINGS_PREFETCH_CONCURRENCY) {
        state.activeBatches += 1;
        return;
    }

    await new Promise<void>((resolve) => {
        state.queuedResolvers.push(resolve);
    });
}

function releaseWagsListingsPrefetchSlot(state: WagsListingsPrefetchState) {
    const resolveNext = state.queuedResolvers.shift();
    if (resolveNext) {
        resolveNext();
        return;
    }

    state.activeBatches = Math.max(0, state.activeBatches - 1);
}

export async function prefetchWagsListings(
    dbClient: DbClient,
    listingIds: string[],
    options?: { fetch?: typeof fetch },
) {
    const uniqueListingIds = [...new Set(listingIds.filter(Boolean))];
    const collection = getWagsListingsCollection(dbClient);
    const state = getWagsListingsPrefetchState(dbClient);
    const pendingPromises = state.pendingWork.forKeys(uniqueListingIds);
    const idsToLoad = uniqueListingIds.filter(
        (listingId) =>
            collection.get(listingId)?.detailHydratedAt == null &&
            !state.pendingWork.has(listingId),
    );

    const chunkCount = Math.ceil(
        idsToLoad.length / WAGS_LISTINGS_PREFETCH_CHUNK_SIZE,
    );
    const chunkPromises = Array.from({ length: chunkCount }, (_, index) =>
        idsToLoad.slice(
            index * WAGS_LISTINGS_PREFETCH_CHUNK_SIZE,
            (index + 1) * WAGS_LISTINGS_PREFETCH_CHUNK_SIZE,
        ),
    ).map((chunk) => {
        return state.pendingWork.track(chunk, async () => {
            await acquireWagsListingsPrefetchSlot(state);
            try {
                const details = await getWagsListingsBatch({
                    data: { listingIds: chunk },
                    ...(options?.fetch ? { fetch: options.fetch } : {}),
                });
                upsertWagsListingDetails(dbClient, details);
            } finally {
                releaseWagsListingsPrefetchSlot(state);
            }
        });
    });

    const prefetchResults = await Promise.allSettled([
        ...new Set(pendingPromises),
        ...chunkPromises,
    ]);

    const rejectedPrefetch = prefetchResults.find(
        (result): result is PromiseRejectedResult =>
            result.status === "rejected",
    );
    if (rejectedPrefetch) {
        throw rejectedPrefetch.reason;
    }

    await dbClient.preloadLiveQuery(
        getWagsListingsLiveQueryOptions(uniqueListingIds),
    );
}

export function prefetchWagsListingsInBackground(
    dbClient: DbClient,
    listingIds: string[],
    options?: { fetch?: typeof fetch },
) {
    void prefetchWagsListings(dbClient, listingIds, options).catch((error) => {
        console.warn("Background prefetchWagsListings failed", error);
    });
}

export const useWagsListingsLiveQuery = (listingIds: string[]) => {
    const query = useLiveQuery(getWagsListingsLiveQueryOptions(listingIds));

    const data = (() => {
        if (query.data == null) {
            return query.data;
        }

        const listingById = new Map(
            query.data.map((listing) => [listing.id, listing] as const),
        );

        return listingIds.flatMap((listingId) => {
            const listing = listingById.get(listingId);
            return listing ? [listing] : [];
        });
    })();

    return { ...query, data };
};
